import { useCallback, useState, useRef, useEffect } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { Check, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import {
  type CreateCalendarEntryData,
  type UpdateType,
  useGetCalendarEntry,
  useGetUserCalendars,
  useUpdateCalendarEntry,
  isManagedExternally,
} from '@zendia/frontend-shared'
import { axiosRequest } from '../../../../utilities/axiosClient'
import {
  CalendarEventForm,
  type CalendarEventFormRef,
} from '../../../../components/Calendar/CalendarEventForm'
import {
  RecurringEventUpdateDialog,
  type RecurringEventUpdateType,
} from '../../../../components/Calendar'
import { validDateParam } from '../../../../components/Calendar/calendarUtils'

export const Route = createFileRoute('/_authenticated/calendar/$entryId/edit')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
  validateSearch: (search: Record<string, unknown>) => {
    return {
      occurrenceId: typeof search.occurrenceId === 'string' ? search.occurrenceId : undefined,
      originalLocalStart:
        typeof search.originalLocalStart === 'string' ? search.originalLocalStart : undefined,
      newStartTime: typeof search.newStartTime === 'string' ? search.newStartTime : undefined,
      updateType:
        typeof search.updateType === 'string' ? (search.updateType as UpdateType) : undefined,
      // The calendar date to return to on save or discard
      returnDate: validDateParam(search.returnDate),
    }
  },
})

function RouteComponent() {
  const { entryId } = Route.useParams()
  const search = Route.useSearch()
  const router = useRouter()
  const { data: entryResponse, isLoading } = useGetCalendarEntry(entryId, axiosRequest, true)
  const { data: calendarsResponse, isLoading: calendarsLoading } = useGetUserCalendars(
    axiosRequest,
    true,
  )
  const { mutateAsync: updateCalendarEntry } = useUpdateCalendarEntry(axiosRequest)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const [showRecurringDialog, setShowRecurringDialog] = useState(false)
  const [pendingData, setPendingData] = useState<
    (CreateCalendarEntryData & { calendarId: string }) | null
  >(null)
  const [hasRecurrenceChange, setHasRecurrenceChange] = useState(false)
  const calendarEventFormRef = useRef<CalendarEventFormRef>(null)

  // An event in a calendar read from an external calendar is managed there. It has no Zendia edit
  // form, however the address was reached, and a drag-and-drop update is never sent for it.
  const isReadOnly = isManagedExternally(
    calendarsResponse?.data?.find((calendar) => calendar.id === entryResponse?.data?.calendarId),
  )

  useEffect(() => {
    if (isReadOnly) {
      router.navigate({ to: '/calendar', search: { date: search.returnDate }, replace: true })
    }
  }, [isReadOnly, router, search.returnDate])

  const onBack = useCallback(() => {
    if (isSubmitting) return
    router.navigate({ to: '/calendar', search: { date: search.returnDate }, replace: true })
  }, [router, isSubmitting, search.returnDate])

  const performUpdate = useCallback(
    async (data: CreateCalendarEntryData & { calendarId: string }, updateType?: UpdateType) => {
      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { calendarId, ...entryData } = data

        // For recurring events editing a single occurrence, use the original occurrence ID
        // The occurrenceId format is: calendarEntryId:originalLocalStart
        let occurrenceId: string | undefined
        if (updateType && updateType !== 'all') {
          // Use the original occurrence ID from search params if available
          // Otherwise fall back to using the provided occurrenceId or generate from original localStart
          if (search.occurrenceId) {
            occurrenceId = search.occurrenceId
          } else if (search.originalLocalStart) {
            occurrenceId = `${entryId}:${search.originalLocalStart}`
          } else if (entryData.localStart) {
            // Fallback for non-recurring or when editing the whole series
            occurrenceId = `${entryId}:${entryData.localStart}`
          }
        }

        await updateCalendarEntry({
          entryId,
          data: {
            ...entryData,
            updateType,
            occurrenceId,
          },
        })
        router.navigate({ to: '/calendar', search: { date: search.returnDate }, replace: true })
      } catch (error) {
        console.error(error)
        setSubmitError('Failed to update calendar event')
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      updateCalendarEntry,
      entryId,
      router,
      search.occurrenceId,
      search.originalLocalStart,
      search.returnDate,
    ],
  )

  const onSave = useCallback(
    async (data: CreateCalendarEntryData & { calendarId: string }) => {
      const entry = entryResponse?.data
      if (!entry) return

      // Check if this is a recurring event
      const isRecurring = entry.recurrence?.rrule && !entry.recurrence.disabled

      if (!isRecurring) {
        // Non-recurring event, update directly
        await performUpdate(data)
        return
      }

      // Check what changed for recurring events
      const recurrenceChanged = JSON.stringify(data.recurrence) !== JSON.stringify(entry.recurrence)

      setHasRecurrenceChange(recurrenceChanged)
      setPendingData(data)
      setShowRecurringDialog(true)
    },
    [entryResponse, performUpdate],
  )

  const handleRecurringUpdateSelect = useCallback(
    async (updateType: RecurringEventUpdateType) => {
      if (!pendingData) return
      await performUpdate(pendingData, updateType as UpdateType)
      setPendingData(null)
      setShowRecurringDialog(false)
    },
    [pendingData, performUpdate],
  )

  // Handle automatic update when coming from drag and drop
  useEffect(() => {
    if (search.newStartTime && search.updateType && entryResponse?.data && !isReadOnly) {
      const entry = entryResponse.data

      // Create the updated data with the new start time
      const updatedData = {
        title: entry.title,
        description: entry.description || '',
        localStart: search.newStartTime,
        duration: entry.duration,
        isAllDay: entry.isAllDay,
        labels: entry.labels || [],
        timeZone: entry.timeZone,
        calendarId: entry.calendarId,
        recurrence: entry.recurrence && !entry.recurrence.disabled ? entry.recurrence : undefined,
      }

      // Perform the update with the specified update type
      performUpdate(updatedData, search.updateType)
    }
  }, [
    search.newStartTime,
    search.updateType,
    entryResponse,
    search.originalLocalStart,
    performUpdate,
    isReadOnly,
  ])

  // Until the calendars are known the form cannot tell whether it may be shown, and a read-only
  // event is on its way back to the calendar
  if (isLoading || calendarsLoading || isReadOnly) {
    return (
      <FullView
        leftAction={
          <ActionButton
            onClick={onBack}
            label="Back"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        centerContent={<div>Edit Event</div>}
        rightAction={<div />}
      >
        <FullViewContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-sm" style={{ color: 'var(--color-body-text)' }}>
              Loading event...
            </div>
          </div>
        </FullViewContent>
      </FullView>
    )
  }

  const entry = entryResponse?.data

  if (!entry) {
    return (
      <FullView
        leftAction={
          <ActionButton
            onClick={onBack}
            label="Back"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        centerContent={<div>Edit Event</div>}
        rightAction={<div />}
      >
        <FullViewContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-sm text-red-600">Event not found</div>
          </div>
        </FullViewContent>
      </FullView>
    )
  }

  // Convert CalendarEntry to form initial data
  // If editing a specific occurrence (originalLocalStart is provided), use that instead of the series start
  const initialData = {
    title: entry.title,
    description: entry.description || '',
    localStart: search.originalLocalStart || entry.localStart,
    duration: entry.duration,
    isAllDay: entry.isAllDay,
    labels: entry.labels || [],
    timeZone: entry.timeZone,
    calendarId: entry.calendarId,
    recurrence: entry.recurrence && !entry.recurrence.disabled ? entry.recurrence : undefined,
  }

  return (
    <>
      <FullView
        leftAction={
          <ActionButton
            onClick={onBack}
            label="Discard"
            icon={<ChevronBackward className="size-6" />}
          />
        }
        centerContent={<div>Edit Event</div>}
        rightAction={
          <ActionButton
            primary
            onClick={() => {
              calendarEventFormRef.current?.submit()
            }}
            label="Save changes"
            disabled={isSubmitting}
            icon={<Check className="m-auto size-6" />}
          />
        }
      >
        <FullViewContent>
          <div className="h-full">
            <CalendarEventForm
              ref={calendarEventFormRef}
              onSubmit={onSave}
              isSubmitting={isSubmitting}
              submitError={submitError}
              initialData={initialData}
            />
          </div>
        </FullViewContent>
      </FullView>

      <RecurringEventUpdateDialog
        open={showRecurringDialog}
        onClose={() => {
          setShowRecurringDialog(false)
          setPendingData(null)
          setIsSubmitting(false)
        }}
        onSelect={handleRecurringUpdateSelect}
        showSingleOption={!hasRecurrenceChange}
      />
    </>
  )
}
